import Skeleton from "@mui/material/Skeleton"
import TransitionFade from "../transition/TransitionFade"
import Box from "@mui/material/Box"

export interface SkeletonLoaderProps extends React.ComponentProps<typeof Skeleton> {
   loading: boolean
   /** 
    * @default true
    */
   transition?: boolean
   /** 
    * Whether to let Skeleton calculate how to render or to use a single line and fill with empty space
    * @default false
    */
   preserveContentBounds?: 'stretch' | 'empty-space' | 'none'
}

type LoaderContentProps = React.PropsWithChildren<{
   skeletonProps: React.ComponentProps<typeof Skeleton>
}> & Pick<SkeletonLoaderProps, 'preserveContentBounds' | 'loading'>

function LoaderContent({ children, skeletonProps, preserveContentBounds, loading }: LoaderContentProps) {
   switch (preserveContentBounds) {
      case 'stretch':
         return <Skeleton {...skeletonProps}>{children}</Skeleton>
      case 'empty-space':
         return <Box position="relative" sx={{ '& > :not(.MuiSkeleton-root)': { visibility: loading ? 'hidden' : 'visible' } }}>
            <Skeleton {...skeletonProps} sx={{ ...skeletonProps.sx, position: 'absolute', top: 0, left: 0, right: 0, display: loading ? 'block' : 'none' }}></Skeleton>
            {children}
         </Box>
      default:
         return <Skeleton {...skeletonProps}></Skeleton>
   }
}

export default function SkeletonLoader({ transition = true, loading, children, preserveContentBounds, ...skeletonProps }: SkeletonLoaderProps) {
   if (transition) {
      return <>
         <TransitionFade visible={loading}>
            <LoaderContent skeletonProps={skeletonProps} loading={loading} preserveContentBounds={preserveContentBounds}>{children}</LoaderContent>
         </TransitionFade>
         <TransitionFade visible={!loading}>
            {children}
         </TransitionFade>
      </>
   }

   return loading
      ? <LoaderContent skeletonProps={skeletonProps} loading={loading}>{children}</LoaderContent>
      : <>{children}</>
}
